import React, { useState, useRef, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { makeQrValue } from '../utils/qrCodes';
import { resolveDisplayNames } from '../utils/labelNames';
import { generateAndPrintLabels } from '../utils/printLabel';
import useScrollLock from '../utils/useScrollLock';
import './ReprintDamagedModal.css';

const MAX_QTY = 50;

/*
  ReprintDamagedModal — reimpresión de etiqueta(s) de repuesto.

  Con el modelo de QR por variación (SH-VAR-{varId}) todas las etiquetas de una
  misma variación son IDÉNTICAS, así que reimprimir una etiqueta dañada = imprimir
  N copias de la etiqueta estándar (la misma que la normal, con su número de lote
  y los nombres/layout que el usuario configuró en el editor).

  Antes este flujo escaneaba códigos por unidad (SH-U) para deducir cuál faltaba;
  eso era incompatible con las etiquetas por variación (todas iguales, no se pueden
  distinguir unidades) y generaba códigos que ni Ventas ni el conteo entendían.

  Props: open, onClose, loteNumero, productName, variationLabel, sku, price,
         storeName, productId, variationId, labelConfig
*/
export default function ReprintDamagedModal({
  open, onClose,
  loteNumero,
  productName, variationLabel,
  sku, price, storeName,
  productId, variationId,
  labelConfig,
}) {
  useScrollLock(open);
  const [qty, setQty] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [done, setDone] = useState(false);
  const qrRef = useRef(null);

  // Resuelve nombre/variación cortos + layout igual que la etiqueta normal.
  const resolved = resolveDisplayNames(productId, variationId, productName, variationLabel);
  const qrValue = makeQrValue(productId, variationId);
  const nPreview = Math.max(1, Math.min(MAX_QTY, parseInt(qty, 10) || 1));

  const handlePrint = useCallback(() => {
    if (printing) return;
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    setPrinting(true);
    try {
      const qrDataUrl = canvas.toDataURL('image/png');
      const item = {
        qrDataUrl,
        productName:    resolved.name,
        variationLabel: resolved.variation,
        price,
        sku,
        loteNumero,
        layout:    resolved.layout || null,
        qrScale:   resolved.qr,
        priceSize: resolved.priceSize,
        nameSize:  resolved.nameSize,
        varSize:   resolved.varSize,
        storeName,
      };
      const items = Array.from({ length: nPreview }, () => item);
      generateAndPrintLabels(items, labelConfig);
      setDone(true);
    } finally {
      setPrinting(false);
    }
  }, [printing, nPreview, resolved, price, sku, loteNumero, storeName, labelConfig]);

  if (!open) return null;

  return (
    <div className="rdm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rdm-card" role="dialog" aria-modal="true">
        <div className="rdm-head">
          <span className="rdm-title">Reimprimir etiqueta</span>
          <button className="rdm-close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="rdm-body">
          {/* Canvas oculto para exportar el QR a PNG */}
          <span className="rdm-canvas-hidden" aria-hidden="true" ref={qrRef}>
            <QRCodeCanvas value={qrValue} size={400} level="M" includeMargin={false} />
          </span>

          <div className="rdm-lot-block">
            {loteNumero && <span className="rdm-lot-tag">Lote: {loteNumero}</span>}
            {resolved.variation && <span className="rdm-var-tag">{resolved.variation}</span>}
          </div>

          <p className="rdm-instruction">
            Todas las etiquetas de esta variación son <strong>idénticas</strong>.
            Indica cuántas etiquetas de repuesto quieres imprimir.
          </p>

          <div className="rdm-qty-row">
            <label htmlFor="rdm-qty">Cantidad</label>
            <input
              id="rdm-qty"
              type="number"
              min="1"
              max={MAX_QTY}
              value={qty}
              onChange={(e) => { setQty(e.target.value); setDone(false); }}
              className="rdm-qty-input"
            />
          </div>

          {done && (
            <p className="rdm-done-msg">
              {nPreview === 1 ? 'Etiqueta enviada a imprimir ✓' : `${nPreview} etiquetas enviadas a imprimir ✓`}
            </p>
          )}

          <div className="rdm-result-footer">
            <button className="rdm-btn rdm-btn--soft" onClick={onClose}>Cerrar</button>
            <button className="rdm-btn rdm-btn--primary" onClick={handlePrint} disabled={printing}>
              {printing
                ? 'Generando…'
                : `Imprimir ${nPreview} etiqueta${nPreview > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
